import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Sparkles, ArrowRight, Minus, Plus, PartyPopper, Lightbulb, Wine, AlertTriangle, TrendingUp } from "lucide-react";
import { toast } from "sonner";
import PreferenceCapture from "@/components/PreferenceCapture";

const CAT_LABEL = {
  starters: "Starters",
  mains: "Mains",
  sides: "Sides & breads",
  desserts: "Desserts",
  drinks_non_alc: "Soft drinks & mocktails",
  drinks_alc: "Wine, beer & cocktails",
};
const CAT_ORDER = ["starters", "mains", "sides", "desserts", "drinks_non_alc", "drinks_alc"];

export default function CuratedMenu() {
  const { bookingId } = useParams();
  const [searchParams] = useSearchParams();
  const initialMode = searchParams.get("mode") === "pick" ? "pick" : "ai";
  const nav = useNavigate();
  const [booking, setBooking] = useState(null);
  const [menuItems, setMenuItems] = useState([]);
  const [mode, setMode] = useState(initialMode);

  // AI mode state
  const [aiMenu, setAiMenu] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);

  // Pick mode state
  const [picks, setPicks] = useState({}); // { menuItemId: qty }
  const [saving, setSaving] = useState(false);
  const [pickResult, setPickResult] = useState(null); // server response after save

  // Per-category caps (server-derived)
  // caps shape: { portion_per_item: {cat: n}, variety: {cat: n}, [cat]: n }
  const [caps, setCaps] = useState({ portion_per_item: {}, variety: {} });

  // Budget feasibility — does the guest's current ₹X/head actually feed `pax` people?
  const [floor, setFloor] = useState(null);
  const [bumping, setBumping] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/bookings/${bookingId}`);
        setBooking(data);
        const rest = await api.get(`/restaurants/${data.restaurant.id}`);
        setMenuItems(rest.data.menu_items);
        const c = await api.get(`/menu-caps?pax=${data.pax}`);
        setCaps(c.data);
        const f = await api.get(`/restaurants/${data.restaurant.id}/budget-floor?pax=${data.pax}`);
        setFloor(f.data);
      } catch (e) {
        toast.error("Booking not found");
        nav("/home");
      }
    })();
  }, [bookingId]); // eslint-disable-line

  const bumpBudget = async () => {
    if (!floor || !booking) return;
    setBumping(true);
    try {
      const { data } = await api.patch(`/bookings/${bookingId}/budget`, {
        budget_per_head: floor.recommended_per_head_inr,
      });
      setBooking(data);
      toast.success(`Budget bumped to ₹${floor.recommended_per_head_inr}/head. Now we can feed the group properly.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update budget");
    } finally {
      setBumping(false);
    }
  };

  const budgetTarget = booking ? booking.pax * booking.budget_per_head_inr : 0;

  const grouped = useMemo(() => {
    return menuItems.reduce((acc, m) => {
      (acc[m.category] = acc[m.category] || []).push(m);
      return acc;
    }, {});
  }, [menuItems]);

  // ── AI mode ──────────────────────────────────────────────
  const buildAI = async () => {
    setAiLoading(true);
    try {
      const prefs = (() => {
        try { return JSON.parse(sessionStorage.getItem("tablo_prefs") || "null"); } catch (_) { return null; }
      })();
      const { data } = await api.post("/curated-menu", { booking_id: bookingId, preferences: prefs });
      setAiMenu(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to build menu");
    } finally {
      setAiLoading(false);
    }
  };

  const aiTotal = aiMenu?.total_price_inr || 0;
  const aiPct = Math.min(100, Math.round((aiTotal / Math.max(1, budgetTarget)) * 100));

  // ── Pick mode ────────────────────────────────────────────
  const setQty = (id, qty, category) => {
    setPicks((p) => {
      const next = { ...p };
      const prev = p[id] || 0;
      if (qty <= 0) {
        delete next[id];
        return next;
      }
      // Portion cap (per dish)
      const portionCap = (caps.portion_per_item || {})[category] || 999;
      if (qty > portionCap) {
        toast.error(`Max ${portionCap} portions of any ${category.replace("_", " ")} for ${booking?.pax || 0} guests.`);
        return p;
      }
      // Variety cap (distinct dishes per category) — only when adding a NEW dish (prev=0)
      if (prev === 0 && qty > 0) {
        const varietyCap = (caps.variety || {})[category] || 999;
        // count current distinct dishes in this category
        const distinct = Object.entries(p).reduce((acc, [k, v]) => {
          if (v > 0 && k !== id) {
            const m = menuItems.find((x) => x.id === k);
            if (m && m.category === category) return acc + 1;
          }
          return acc;
        }, 0);
        if (distinct >= varietyCap) {
          toast.error(`Max ${varietyCap} different ${category.replace("_", " ")} dishes for ${booking?.pax || 0} guests. Trim or switch to Tablo Curate.`);
          return p;
        }
      }
      next[id] = qty;
      return next;
    });
  };

  const switchToCurated = async () => {
    try {
      await api.post(`/bookings/${bookingId}/switch-to-curated`);
      toast.success("Switched to Tablo Curate. Continue to deposit.");
      nav(`/checkout/${bookingId}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to switch");
    }
  };

  const pickTotal = useMemo(() => {
    return Object.entries(picks).reduce((sum, [id, qty]) => {
      const m = menuItems.find((x) => x.id === id);
      return sum + (m ? m.price_inr * qty : 0);
    }, 0);
  }, [picks, menuItems]);

  const pickCount = useMemo(
    () => Object.values(picks).reduce((s, q) => s + q, 0),
    [picks]
  );

  const pickPct = Math.min(100, Math.round((pickTotal / Math.max(1, budgetTarget)) * 100));

  const savePicks = async () => {
    if (pickCount === 0) return toast.error("Add at least one item");
    setSaving(true);
    try {
      const items = Object.entries(picks).map(([menu_item_id, qty]) => ({ menu_item_id, qty }));
      const { data } = await api.post("/menu-selection", { booking_id: bookingId, items });
      setPickResult(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to save selection");
    } finally {
      setSaving(false);
    }
  };

  if (!booking) return <div className="py-10 text-light-ink animate-pulse">Loading...</div>;

  return (
    <div className="space-y-6 animate-fade-up pb-40">
      <header>
        <p className="text-amber text-xs uppercase tracking-[0.18em] font-medium mb-2">Menu for your booking</p>
        <h1 className="font-serif text-3xl sm:text-4xl leading-tight">
          {booking.pax} guests at {booking.restaurant?.name}.
        </h1>
        <p className="text-light-ink text-sm mt-2">
          Let Tablo build a balanced set menu within ±5% of your ₹{booking.budget_per_head_inr}/head budget — or pick the dishes yourself if you know exactly what you want.
        </p>
      </header>

      {/* Feasibility banner — warn if budget can't feed the group */}
      {floor && booking && booking.budget_per_head_inr < floor.min_decent_per_head_inr && (
        <div className="rounded-2xl border border-amber/40 bg-amber-soft/30 p-4 sm:p-5 flex gap-3 items-start" data-testid="feasibility-banner">
          <AlertTriangle className="w-5 h-5 text-amber shrink-0 mt-0.5" strokeWidth={1.8}/>
          <div className="flex-1">
            <p className="font-medium text-ink leading-snug">
              Heads-up — ₹{booking.budget_per_head_inr}/head likely won't feed {booking.pax} guests properly here.
            </p>
            <p className="text-sm text-light-ink mt-1 leading-relaxed">
              A decent shared meal at <span className="font-medium text-ink">{booking.restaurant.name}</span> for {booking.pax} pax needs about{" "}
              <span className="font-medium text-ink">₹{floor.min_decent_per_head_inr}/head</span> (≈ ₹{floor.min_decent_total_inr.toLocaleString("en-IN")} total) — that's
              {" "}~{floor.breakdown.reduce((s, b) => s + b.portions, 0)} portions across starters, mains, sides, desserts & drinks.
              Bump your budget so Tablo Curate AI can balance the menu without skipping categories.
            </p>
            <div className="mt-3 flex flex-wrap gap-2 items-center">
              <button
                onClick={bumpBudget}
                disabled={bumping}
                className="tablo-btn-primary text-sm inline-flex items-center gap-1.5"
                data-testid="feasibility-bump-btn"
              >
                <TrendingUp className="w-4 h-4" strokeWidth={1.8}/>
                {bumping ? "Updating..." : `Bump to ₹${floor.recommended_per_head_inr}/head`}
              </button>
              <span className="text-xs text-light-ink">
                +₹{floor.recommended_per_head_inr - booking.budget_per_head_inr}/head · total ₹{(floor.recommended_per_head_inr * booking.pax).toLocaleString("en-IN")}
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Recommended banner — soft hint when budget is fine but on the lower edge */}
      {floor && booking
        && booking.budget_per_head_inr >= floor.min_decent_per_head_inr
        && booking.budget_per_head_inr < floor.recommended_per_head_inr && (
        <div className="rounded-2xl border border-tablo-border bg-cream p-3 sm:p-4 flex gap-2 items-start text-sm" data-testid="feasibility-hint">
          <Lightbulb className="w-4 h-4 text-amber shrink-0 mt-0.5" strokeWidth={1.6}/>
          <p className="text-light-ink leading-snug">
            ₹{booking.budget_per_head_inr}/head works for {booking.pax} guests here, but ₹{floor.recommended_per_head_inr}/head gives Tablo more room to add favourites and signatures.
            <button onClick={bumpBudget} className="text-amber underline ml-1" data-testid="feasibility-bump-link">Bump to ₹{floor.recommended_per_head_inr}</button>
          </p>
        </div>
      )}

      {/* Preference capture — sets allergens, spice, veg, drink prefs */}
      <PreferenceCapture
        defaultAllergens={booking?.restaurant?.default_allergens || []}
        testid="curated-prefs"
      />

      <Tabs value={mode} onValueChange={setMode}>
        <TabsList className="bg-cream border border-tablo-border w-full grid grid-cols-2">
          <TabsTrigger value="ai" data-testid="tab-mode-ai" className="flex items-center gap-1.5">
            <Sparkles className="w-3.5 h-3.5" strokeWidth={1.6}/> Tablo Curate
          </TabsTrigger>
          <TabsTrigger value="pick" data-testid="tab-mode-pick">Pick myself</TabsTrigger>
        </TabsList>

        {/* ── AI MODE ─────────────────────────────────────────── */}
        <TabsContent value="ai" className="space-y-5 mt-5">
          {!aiMenu && (
            <Button onClick={buildAI} disabled={aiLoading} className="w-full tablo-btn-primary" data-testid="curated-build-btn">
              <Sparkles className="w-4 h-4 mr-2" strokeWidth={1.6}/>
              {aiLoading ? "Curating..." : "Build my menu"}
            </Button>
          )}

          {aiMenu && (
            <>
              <div className="space-y-1">
                {(aiMenu.items || []).map((it, idx) => (
                  <div key={idx} className="flex items-center justify-between py-3 border-b border-tablo-border" data-testid={`menu-item-${idx}`}>
                    <div className="flex items-center gap-2">
                      <span className={`inline-block w-2 h-2 rounded-full ${it.veg_flag ? "bg-green-600" : "bg-rose-600"}`}/>
                      <div>
                        <p className="font-medium text-ink">{it.name}</p>
                        <p className="text-xs text-light-ink uppercase tracking-wider">{(it.category || "").replace("_", " ")} · qty {it.qty}</p>
                      </div>
                    </div>
                    <p className="text-sm text-light-ink">₹{(it.line_total || 0).toLocaleString("en-IN")}</p>
                  </div>
                ))}
              </div>

              <div className="tablo-card p-5">
                <div className="flex items-baseline justify-between mb-2">
                  <p className="text-xs uppercase tracking-wider text-light-ink">Total · {aiMenu.items?.length || 0} items</p>
                  <p className="font-serif text-2xl">₹{aiTotal.toLocaleString("en-IN")}</p>
                </div>
                <div className="w-full h-2 bg-tablo-border rounded-full overflow-hidden">
                  <div className="h-full bg-amber rounded-full transition-all" style={{ width: `${aiPct}%` }} />
                </div>
                <div className="flex justify-between text-xs text-light-ink mt-2">
                  <span>Budget target ₹{budgetTarget.toLocaleString("en-IN")}</span>
                  <span className={Math.abs(aiMenu.delta_pct || 0) <= 5 ? "text-green-700" : "text-amber"}>
                    {aiMenu.delta_pct >= 0 ? "+" : ""}{aiMenu.delta_pct}% vs budget
                  </span>
                </div>
                {aiMenu.savings_vs_self_pick_inr > 0 && (
                  <div className="mt-3 bg-amber-soft/25 border border-amber-soft/50 rounded-xl p-3 flex gap-2 text-sm" data-testid="ai-savings-card">
                    <PartyPopper className="w-4 h-4 text-amber mt-0.5 shrink-0" strokeWidth={1.6}/>
                    <p className="text-ink">{aiMenu.savings_message}</p>
                  </div>
                )}
              </div>

              <Button onClick={() => nav(`/checkout/${bookingId}`)} className="w-full tablo-btn-primary" data-testid="curated-continue-btn">
                Continue to deposit <ArrowRight className="w-4 h-4 ml-2" strokeWidth={2}/>
              </Button>
            </>
          )}
        </TabsContent>

        {/* ── PICK MODE ───────────────────────────────────────── */}
        <TabsContent value="pick" className="space-y-6 mt-5">
          {pickResult ? (
            <>
              <div className={`tablo-card p-5 flex gap-3 items-start ${
                pickResult.message_kind === "tablo_would_save" ? "border-amber/60 bg-amber-soft/15" :
                pickResult.message_kind === "guest_saved" ? "border-green-700/40 bg-green-50" :
                ""
              }`} data-testid="pick-result-card">
                {pickResult.message_kind === "guest_saved" ? (
                  <PartyPopper className="w-5 h-5 text-green-700 mt-0.5 shrink-0" strokeWidth={1.6}/>
                ) : pickResult.message_kind === "tablo_would_save" ? (
                  <Lightbulb className="w-5 h-5 text-amber mt-0.5 shrink-0" strokeWidth={1.6}/>
                ) : (
                  <Sparkles className="w-5 h-5 text-amber mt-0.5 shrink-0" strokeWidth={1.6}/>
                )}
                <div className="flex-1">
                  <p className="font-medium">{pickResult.message}</p>
                  <div className="flex gap-4 text-xs text-light-ink mt-2 flex-wrap">
                    <span>Your picks: <span className="font-medium text-ink">₹{pickResult.total_price_inr.toLocaleString("en-IN")}</span></span>
                    {pickResult.mrp_savings_inr > 0 && (
                      <span className="text-amber">Off MRP: <span className="font-medium">−₹{pickResult.mrp_savings_inr.toLocaleString("en-IN")}</span></span>
                    )}
                    <span>Tablo Curate would be: <span className="font-medium text-ink">₹{pickResult.ai_alternative_total_inr.toLocaleString("en-IN")}</span></span>
                  </div>
                  {pickResult.message_kind === "tablo_would_save" && (
                    <button
                      onClick={switchToCurated}
                      className="mt-3 w-full tablo-btn-primary text-sm inline-flex items-center justify-center gap-1.5"
                      data-testid="pick-switch-to-curated-btn"
                    >
                      <Sparkles className="w-4 h-4" strokeWidth={1.6}/>
                      Save ₹{Math.abs(pickResult.delta_inr).toLocaleString("en-IN")} · Switch to Tablo Curate
                    </button>
                  )}
                </div>
              </div>
              <Button onClick={() => nav(`/checkout/${bookingId}`)} className="w-full tablo-btn-primary" data-testid="pick-continue-btn">
                Continue to deposit <ArrowRight className="w-4 h-4 ml-2" strokeWidth={2}/>
              </Button>
              <button onClick={() => setPickResult(null)} className="text-sm text-light-ink underline w-full text-center" data-testid="pick-edit-link">
                Edit my picks
              </button>
            </>
          ) : (
            <>
              <p className="text-light-ink text-sm">Tap + and − to set quantities. We've pre-loaded the full menu including wine, beer, and cocktails where available.</p>
              {CAT_ORDER.filter((c) => grouped[c]?.length).map((cat) => {
                const portionCap = (caps.portion_per_item || {})[cat] || 0;
                const varietyCap = (caps.variety || {})[cat] || 0;
                const distinctPicked = grouped[cat].reduce((s, m) => s + ((picks[m.id] || 0) > 0 ? 1 : 0), 0);
                const totalPortions = grouped[cat].reduce((s, m) => s + (picks[m.id] || 0), 0);
                const varietyAtCap = distinctPicked >= varietyCap && varietyCap > 0;
                return (
                <div key={cat}>
                  <div className="flex items-center justify-between mb-2 gap-3">
                    <div className="flex items-center gap-2">
                      {cat === "drinks_alc" && <Wine className="w-4 h-4 text-amber" strokeWidth={1.6}/>}
                      <p className="text-xs uppercase tracking-[0.18em] text-amber font-medium">{CAT_LABEL[cat]}</p>
                    </div>
                    {varietyCap > 0 && (
                      <span className="text-[10px] uppercase tracking-wider text-light-ink text-right" data-testid={`cap-${cat}`}>
                        <span className={varietyAtCap ? "text-amber font-medium" : "text-ink font-medium"}>{distinctPicked}/{varietyCap}</span> dishes
                        <span className="text-light-ink/70"> · {portionCap} portions each</span>
                        {totalPortions > 0 && <><br/><span className="text-light-ink/70">{totalPortions} portions picked</span></>}
                      </span>
                    )}
                  </div>
                  <div className="tablo-card divide-y divide-tablo-border/60">
                    {grouped[cat].map((m) => {
                      const qty = picks[m.id] || 0;
                      const portionCap = (caps.portion_per_item || {})[m.category] || 999;
                      const atPortionCap = qty >= portionCap;
                      const blockedByVariety = qty === 0 && varietyAtCap;
                      const disablePlus = atPortionCap || blockedByVariety;
                      const plusTitle = atPortionCap
                        ? `Max ${portionCap} portions for ${booking?.pax} guests`
                        : blockedByVariety
                          ? `Max ${varietyCap} different ${cat.replace("_", " ")} dishes`
                          : "";
                      return (
                        <div key={m.id} className="flex justify-between items-center p-4" data-testid={`pick-item-${m.id}`}>
                          <div className="flex items-start gap-2 flex-1 min-w-0">
                            <span className={`mt-1.5 inline-block w-2 h-2 rounded-full shrink-0 ${m.veg_flag ? "bg-green-600" : "bg-rose-600"}`}/>
                            <div className="min-w-0">
                              <p className="text-sm font-medium text-ink">{m.name}</p>
                              <p className="text-xs text-light-ink">
                                {m.list_price_inr > m.price_inr && (
                                  <span className="line-through text-light-ink/60 mr-1">₹{m.list_price_inr}</span>
                                )}
                                <span className="font-medium text-ink">₹{m.price_inr}</span>
                                {m.list_price_inr > m.price_inr && (
                                  <span className="text-amber ml-1">· save ₹{m.savings_inr}</span>
                                )}
                                {m.tags?.length > 0 && <span className="text-light-ink/70"> · {m.tags.join(", ")}</span>}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2 shrink-0">
                            <button
                              onClick={() => setQty(m.id, qty - 1, m.category)}
                              disabled={qty === 0}
                              className="w-7 h-7 rounded-full border border-tablo-border flex items-center justify-center disabled:opacity-40 hover:bg-cream"
                              data-testid={`pick-decr-${m.id}`}
                            >
                              <Minus className="w-3 h-3" strokeWidth={1.6}/>
                            </button>
                            <span className="w-6 text-center text-sm font-medium" data-testid={`pick-qty-${m.id}`}>{qty}</span>
                            <button
                              onClick={() => setQty(m.id, qty + 1, m.category)}
                              disabled={disablePlus}
                              className="w-7 h-7 rounded-full bg-ink text-cream flex items-center justify-center hover:bg-light-ink disabled:opacity-40"
                              data-testid={`pick-incr-${m.id}`}
                              title={plusTitle}
                            >
                              <Plus className="w-3 h-3" strokeWidth={1.8}/>
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
                );
              })}
            </>
          )}
        </TabsContent>
      </Tabs>

      {/* Sticky total bar when in pick mode and no result yet */}
      {mode === "pick" && !pickResult && (
        <div className="fixed left-0 right-0 bottom-16 sm:bottom-20 z-30 px-5 pointer-events-none" data-testid="pick-sticky-bar">
          <div className="max-w-3xl mx-auto tablo-card p-4 pointer-events-auto shadow-tablo-lg">
            <div className="flex items-baseline justify-between mb-2">
              <p className="text-xs uppercase tracking-wider text-light-ink">{pickCount} items selected</p>
              <p className="font-serif text-xl">₹{pickTotal.toLocaleString("en-IN")}</p>
            </div>
            <div className="w-full h-1.5 bg-tablo-border rounded-full overflow-hidden">
              <div className="h-full bg-amber rounded-full transition-all" style={{ width: `${pickPct}%` }} />
            </div>
            <div className="flex justify-between text-[11px] text-light-ink mt-1.5">
              <span>Budget ₹{budgetTarget.toLocaleString("en-IN")}</span>
              <span>{pickPct}% of budget</span>
            </div>
            <Button
              onClick={savePicks}
              disabled={saving || pickCount === 0}
              className="w-full tablo-btn-primary mt-3 text-sm"
              data-testid="pick-save-btn"
            >
              {saving ? "Saving..." : "Save my menu & see comparison"}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
